// When is a game over, for the purpose of sending it?
//
// **A separate question from every other "is it over" in the codebase, and it
// has to be.** `runIsOver` decides whether to keep a save, `gameStatus`
// decides whether the board accepts moves, and `stageTurnsOver` decides
// whether a camp lands. This one decides whether a recording leaves somebody's
// machine, and it is deliberately the *narrowest* of them: a game that is
// merely paused, abandoned, or still being watched is not finished, and
// sending it would be sending a game the player has not finished playing.
//
// Pure, so the rule is testable without a browser — and so the one thing that
// must never fire twice can be reasoned about at all.

import type { GameState } from "src/engine/rules/commands";

import type { GameKind } from "./submission";

/**
 * Modes as the app knows them, mapped to what a submission is filed as.
 *
 * `watch-run` and `watch` both collapse to `"watch"`: nobody played them, so
 * they are a recording of the machine playing itself. Kept rather than dropped
 * because a machine game that soft-locks is exactly the bug worth having a
 * log of — and separated from the rest because counting them as play would
 * quietly claim a player did something they did not.
 */
export function kindOf(mode: string): GameKind | null {
  switch (mode) {
    case "run":
      return "run";
    case "solo":
      return "scenario";
    case "net":
      return "versus";
    case "local":
      return "local";
    case "watch":
    case "watch-run":
      return "watch";
    default:
      // The menu, the scenario screen, the rules page. Not games.
      return null;
  }
}

/**
 * Has this game reached an ending?
 *
 * A **run** ends when it is won or lost — which is not the same as the board
 * being unplayable, since a run survives its own stage boundaries and a camp
 * being mated is a front turning over rather than an ending.
 *
 * Everything else ends when the *chess* does: mate, stalemate, or one of the
 * three draws.
 *
 * **`state.status` is read, never recomputed.** The reducer maintains it on
 * every command with the run's own draw suppression already applied
 * (`commands.ts`, `gameStatus(..., !state.run)`), so recomputing here would be
 * a second reading of the same question that can disagree with the board the
 * player is looking at. `"check"` is not an ending, which is why this is a set
 * of endings rather than `!== "playing"`.
 *
 * **`runOver` is passed in rather than imported.** `runIsOver` lives in
 * `src/client/ai/ui/`, and `src/engine/telemetry/` importing the UI to answer a question about
 * the rules is the wrong direction — `scorecard.ts` and `mechanics.ts` both
 * depend on nothing but `src/engine/rules/`, and this file keeping that property is
 * what lets the whole module be tested in the node environment.
 */
const ENDINGS = new Set([
  "checkmate",
  "stalemate",
  "draw-fifty-move",
  "draw-repetition",
  "draw-material"
]);

export function gameIsFinished(state: GameState, runOver: boolean): boolean {
  if (state.run) return runOver;
  return ENDINGS.has(state.status);
}
